/**
 * Yönetici Ekip Raporu — Spec Bölüm 6.1.2 + S4.2
 *
 *   /yonetici/sinavlar/:id/rapor
 *
 * - Rapor yoksa "Rapor Üret" butonu → POST /generate-report
 * - Rapor varsa markdown render + meta (cost, model, tarih)
 * - 5sn polling yeniden gönderme
 */
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'wouter';
import { Header } from '../components/Header.js';
import { Alert, Button, Card } from '../components/ui.js';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../lib/auth-client.js';
import { ReportMarkdown } from '../components/ReportMarkdown.js';
import { trackEngagement } from '../lib/engagement.js';

interface ReportData {
  examId: string;
  markdown: string | null;
  generatedAt: string | null;
  promptVersion: string | null;
  costUsd: number | null;
}

export function ExamTeamReportPage(): JSX.Element {
  const [, setLocation] = useLocation();
  const { data: session, isPending: sessionPending } = useSession();
  const params = useParams<{ id: string }>();
  const examId = params.id ?? '';
  const [data, setData] = useState<ReportData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [pollCount, setPollCount] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const result = await api.get<ReportData>(
        `/api/manager/exams/${examId}/report`,
      );
      setData(result);
      if (result.markdown) {
        trackEngagement('team_report_viewed', { examId });
      }
      return result;
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Rapor yüklenemedi');
      return null;
    }
  }, [examId]);

  useEffect(() => {
    if (sessionPending) return;
    if (!session?.user) {
      setLocation('/yonetici/giris');
      return;
    }
    void refresh();
  }, [session, sessionPending, refresh, setLocation]);

  // Markdown yoksa 5sn'de bir poll (max 20)
  useEffect(() => {
    if (!data) return;
    if (data.markdown) return;
    if (!generating && pollCount === 0) return;
    if (pollCount >= 20) return;
    const t = setTimeout(async () => {
      const next = await refresh();
      if (next?.markdown) {
        setGenerating(false);
      }
      setPollCount((c) => c + 1);
    }, 5000);
    return () => clearTimeout(t);
  }, [data, generating, pollCount, refresh]);

  async function triggerGenerate(): Promise<void> {
    setError(null);
    setGenerating(true);
    setPollCount(1);
    try {
      await api.post(`/api/manager/exams/${examId}/generate-report`);
      trackEngagement('team_report_generated', { examId });
    } catch (err) {
      setGenerating(false);
      if (err instanceof ApiError) setError(err.message);
      else setError('Rapor üretimi başlatılamadı');
    }
  }

  if (sessionPending || (!data && !error)) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-4xl px-6 py-12">
          <p className="text-slate-500">Yükleniyor…</p>
        </main>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-4xl px-6 py-12">
          <Alert variant="danger">{error}</Alert>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="mx-auto max-w-4xl px-6 py-12">
        <button
          type="button"
          onClick={() => setLocation(`/yonetici/sinavlar/${examId}`)}
          className="text-sm font-medium text-blue-900 hover:underline"
        >
          ← Sınav Detayı
        </button>

        <h1 className="mt-2 text-3xl font-bold text-slate-900">Ekip Raporu</h1>
        <p className="mt-1 text-slate-600">
          AI tarafından üretilmiş yönetici özetiniz. Sektör karşılaştırma, hata
          örüntüleri ve eğitim önerileri.
        </p>

        {error && (
          <div className="mt-6">
            <Alert variant="danger">{error}</Alert>
          </div>
        )}

        {data?.markdown ? (
          <>
            <Card className="mt-6">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 pb-3 text-xs text-slate-500">
                <span>
                  Oluşturuldu:{' '}
                  {data.generatedAt
                    ? new Date(data.generatedAt).toLocaleString('tr-TR')
                    : '—'}
                </span>
                <span>
                  Prompt: {data.promptVersion ?? '—'}
                  {data.costUsd !== null && (
                    <>
                      {' · '}
                      Maliyet:{' '}
                      {data.costUsd === 0
                        ? 'mock'
                        : `$${data.costUsd.toFixed(4)}`}
                    </>
                  )}
                </span>
              </div>
              <ReportMarkdown text={data.markdown} />
            </Card>

            <div className="mt-6 flex items-center justify-end gap-2">
              <Button
                variant="secondary"
                onClick={triggerGenerate}
                loading={generating}
              >
                Raporu Yenile
              </Button>
              <Button onClick={() => window.print()}>Yazdır / PDF</Button>
            </div>
          </>
        ) : (
          <Card className="mt-6">
            {generating ? (
              <>
                <div className="flex items-center gap-3">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-900 border-t-transparent" />
                  <p className="text-slate-700">
                    Rapor üretiliyor… (kontrol {pollCount}/20)
                  </p>
                </div>
                <p className="mt-3 text-sm text-slate-500">
                  Gateway client (mock) ekip verisini analiz ediyor. Üretim
                  ortamında Claude Opus 4.7 kullanılır. Sayfa otomatik
                  yenilenecek.
                </p>
              </>
            ) : (
              <>
                <h2 className="text-lg font-semibold text-slate-900">
                  Rapor henüz üretilmedi
                </h2>
                <p className="mt-2 text-sm text-slate-600">
                  En az 1 aday sınavı tamamladığında ekip raporu
                  üretilebilir. Aşağıdaki butona basarak AI raporunu
                  başlatın.
                </p>
                <Button onClick={triggerGenerate} className="mt-4">
                  Ekip Raporunu Üret
                </Button>
              </>
            )}
          </Card>
        )}
      </main>
    </div>
  );
}
